// src/services/indexedDB/repositories/eventRepository.ts
// Event 專屬邏輯（與 TimeBox Repository 行為一致）

import { Event } from '../../../types/calendar-event';
import { BaseRepository } from '../core/base-repository';
import { STORE_NAMES, initDB } from '../core/database';
import { CachedEntity } from '../core/types';
import { formatDateString } from '../utils/date-helpers';
import {
  isRecentlyDeleted,
  markAsRecentlyDeleted,
  shouldSkipRemote,
} from '../utils/cache-manager';
import { logError, logDev } from '../../../utils/logger';

/**
 * Event with dateString for indexing
 */
interface EventWithDate extends Event {
  dateString?: string;
}

/**
 * Event Repository
 * 處理 Event 相關的 IndexedDB 操作
 */
class EventRepository extends BaseRepository<Event> {
  protected readonly storeName = STORE_NAMES.EVENTS;

  /**
   * 準備 Event 以儲存（添加 dateString）
   */
  protected prepareForStorage(entity: Event): CachedEntity<Event> {
    const prepared = super.prepareForStorage(entity) as CachedEntity<EventWithDate>;

    // 添加 dateString 用於索引查詢
    const startDate =
      entity.startTime instanceof Date ? entity.startTime : new Date(entity.startTime);
    prepared.dateString = formatDateString(startDate);

    // 序列化時間欄位（IndexedDB 需要存為 number）
    if (entity.startTime instanceof Date) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (prepared as CachedEntity<EventWithDate>).startTime = entity.startTime.getTime() as unknown as Date;
    }
    if (entity.endTime instanceof Date) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (prepared as CachedEntity<EventWithDate>).endTime = entity.endTime.getTime() as unknown as Date;
    }

    return prepared as CachedEntity<Event>;
  }

  /**
   * 反序列化 Event（移除 dateString 並轉換日期）
   */
  protected deserialize(stored: CachedEntity<Event>): Event {
    const { cachedAt, dateString, ...entity } = stored as CachedEntity<EventWithDate>;

    // 轉換日期欄位
    return {
      ...entity,
      startTime:
        typeof entity.startTime === 'number' ? new Date(entity.startTime) : entity.startTime,
      endTime: typeof entity.endTime === 'number' ? new Date(entity.endTime) : entity.endTime,
      createdAt:
        typeof entity.createdAt === 'number' ? new Date(entity.createdAt) : entity.createdAt,
      updatedAt: entity.updatedAt
        ? typeof entity.updatedAt === 'number'
          ? new Date(entity.updatedAt)
          : entity.updatedAt
        : undefined,
    };
  }

  /**
   * 批量保存 Events（帶衝突檢測）
   */
  async saveMany(userId: string, events: Event[]): Promise<void> {
    if (!events.length) return;

    try {
      const db = await initDB();
      const tx = db.transaction(this.storeName, 'readwrite');
      const store = tx.objectStore(this.storeName);

      const promises: Promise<void>[] = [];

      for (const event of events) {
        // 跳過最近被刪除的 Event
        if (isRecentlyDeleted('Event', event.id)) {
          logDev('Skipping recently deleted Event', { id: event.id });
          continue;
        }

        // 檢查本地是否有更新的版本
        const promise = new Promise<void>((resolve) => {
          const getRequest = store.get(event.id);

          getRequest.onsuccess = () => {
            const existing = getRequest.result;

            // 衝突檢測
            if (shouldSkipRemote(existing, event)) {
              resolve();
              return;
            }

            // 保存雲端數據
            const prepared = this.prepareForStorage({ ...event, userId } as Event);
            const putRequest = store.put(prepared);

            putRequest.onsuccess = () => resolve();
            putRequest.onerror = () => resolve(); // 單個失敗不影響整體
          };

          getRequest.onerror = () => {
            // 讀取失敗，保守處理：保存雲端數據
            const prepared = this.prepareForStorage({ ...event, userId } as Event);
            store.put(prepared);
            resolve();
          };
        });

        promises.push(promise);
      }

      // 等待所有操作完成
      await Promise.all(promises);

      return new Promise((resolve, reject) => {
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
    } catch (error) {
      logError('Failed to save Events to IndexedDB', error);
      throw error;
    }
  }

  /**
   * 按日期獲取 Events，含跨日 all-day event 補查
   */
  async getByDate(userId: string, date: Date): Promise<Event[]> {
    try {
      const db = await initDB();
      const tx = db.transaction(this.storeName, 'readonly');
      const store = tx.objectStore(this.storeName);

      const dateString = formatDateString(date);
      const dayStart = new Date(date);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(date);
      dayEnd.setHours(23, 59, 59, 999);

      // 第一段：當天 startTime 的 events（timed + allday 第一天）
      const byDateRequest = store.index('userId_date').getAll([userId, dateString]);
      const byDateResults = await new Promise<CachedEntity<Event>[]>((resolve, reject) => {
        byDateRequest.onsuccess = () => resolve(byDateRequest.result);
        byDateRequest.onerror = () => reject(byDateRequest.error);
      });

      // 第二段：補查跨日 allday event（startTime 不在當天但區間涵蓋當天）
      const allRequest = store.index('userId').getAll(userId);
      const allResults = await new Promise<CachedEntity<Event>[]>((resolve, reject) => {
        allRequest.onsuccess = () => resolve(allRequest.result);
        allRequest.onerror = () => reject(allRequest.error);
      });

      const crossDayAlldayIds = new Set(byDateResults.map(e => e.id));
      const crossDayAllday = allResults.filter(e => {
        if (e.timeType !== 'allday') return false;
        if (crossDayAlldayIds.has(e.id)) return false; // 已在第一段，跳過
        const eventStart = typeof e.startTime === 'number' ? e.startTime : Number(e.startTime);
        const eventEnd = typeof e.endTime === 'number' ? e.endTime : Number(e.endTime);
        return eventStart <= dayEnd.getTime() && eventEnd >= dayStart.getTime();
      });

      return [...byDateResults, ...crossDayAllday].map(item => this.deserialize(item));
    } catch (error) {
      logError('Failed to get Events by date', error);
      throw error;
    }
  }

  /**
   * 刪除 Event（帶防護標記）
   */
  async deleteLocal(id: string): Promise<void> {
    markAsRecentlyDeleted('Event', id);
    await super.deleteLocal(id);
  }

  /**
   * 檢查是否最近被刪除
   */
  isRecentlyDeleted(id: string): boolean {
    return isRecentlyDeleted('Event', id);
  }
}

// 導出單例
export const eventRepository = new EventRepository();
